import { useEffect, useRef, useState } from "react";
import { Combine, Loader2, Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusDot } from "@/components/orders/status-badge";
import { useMergeOrders } from "@/hooks/use-meta-orders";
import { codTotal, computeShippingTargets } from "@/lib/meta-orders/shipping-rule";
import { cn, formatCurrency, plural } from "@/lib/utils";
import type { CandidateGroup, CandidateOrder, MetaAddress } from "@/services/meta-orders";

const km = (v: number) => formatCurrency(v, "KM");
const keyOf = (o: { storeId: string; orderId: string }) => `${o.storeId}:${o.orderId}`;
const REASON_LABEL: Record<string, string> = { phone: "telefon", email: "email", name: "ime" };

/** Polja koja idu na naljepnicu; email se ne štampa, pa ne pravi "drugu" adresu. */
const ADDRESS_KEY_FIELDS = ["firstName", "lastName", "address1", "address2", "city", "postcode", "phone"] as const;
const addressKey = (a: MetaAddress) =>
  ADDRESS_KEY_FIELDS.map((f) => (a[f] || "").trim().toLowerCase().replace(/\s+/g, " ")).join("|");

export function formatAddress(a: MetaAddress): string {
  const name = [a.firstName, a.lastName].filter(Boolean).join(" ");
  const street = [a.address1, a.address2].filter(Boolean).join(" ");
  const city = [a.postcode, a.city].filter(Boolean).join(" ");
  return [name, street, city, a.phone].filter(Boolean).join(", ");
}

const unique = (values: string[]) => [...new Set(values.map((v) => v.trim()).filter(Boolean))];

/**
 * Pregled spajanja računat istim pravilom kao server: koja narudžba zadržava poštarinu,
 * otkupnina nakon skidanja poštarine i koliko kupac uštedi.
 */
function previewMerge(orders: CandidateOrder[]) {
  const targets = computeShippingTargets(
    orders.map((o) => ({
      key: keyOf(o),
      isPaid: o.isPaid,
      originalShipping: o.shippingTotal,
      dateCreated: o.dateCreated,
      wcOrderId: Number(o.orderId),
    })),
  );
  const cod = orders.filter((o) => !o.isPaid);
  const keeper = cod.find((o) => (targets[keyOf(o)] ?? 0) > 0) ?? null;
  const savings = cod.reduce((s, o) => s + (o.shippingTotal - (targets[keyOf(o)] ?? 0)), 0);
  const newCod = codTotal(orders.map((o) => ({ isPaid: o.isPaid, total: o.total - o.shippingTotal + (targets[keyOf(o)] ?? 0) })));
  return {
    targets,
    keeper,
    savings: Math.round(savings * 100) / 100,
    newCod,
    hasCod: cod.length > 0,
    // Pravilo: besplatna dostava ima prednost nad već plaćenom poštarinom.
    freeShipping: orders.some((o) => o.shippingTotal === 0),
  };
}

type Props = { group: CandidateGroup; highlighted?: boolean };

export function CandidateGroupCard({ group, highlighted }: Props) {
  const merge = useMergeOrders();
  const ref = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(group.orders.map(keyOf)));
  const [addressChoice, setAddressChoice] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Broj nove meta narudžbe: kartica ostaje zaključana dok je osvježena lista kandidata ne ukloni.
  const [mergedInto, setMergedInto] = useState<string | null>(null);

  useEffect(() => {
    if (highlighted) ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlighted]);

  const chosenOrders = group.orders.filter((o) => selected.has(keyOf(o)));
  const preview = previewMerge(chosenOrders);

  // Adrese se nude samo iz označenih narudžbi; zadana je adresa najnovije narudžbe.
  const addressOptions = (() => {
    const seen = new Map<string, { address: MetaAddress; orders: CandidateOrder[] }>();
    for (const o of [...chosenOrders].reverse()) {
      const k = addressKey(o.address);
      const entry = seen.get(k);
      if (entry) entry.orders.push(o);
      else seen.set(k, { address: o.address, orders: [o] });
    }
    return [...seen.entries()].map(([key, v]) => ({ key, ...v }));
  })();

  const effectiveAddress =
    addressOptions.find((a) => a.key === addressChoice) ?? addressOptions[0] ?? null;

  const latest = group.orders[group.orders.length - 1]!;
  const phones = unique(group.orders.map((o) => o.phone));
  const emails = unique(group.orders.map((o) => o.email));
  const cities = unique(group.orders.map((o) => o.address.city));
  const recipientEmails = unique(chosenOrders.map((o) => o.email));
  const addressComplete =
    !!effectiveAddress &&
    !!(effectiveAddress.address.firstName || effectiveAddress.address.lastName) &&
    !!effectiveAddress.address.address1 &&
    !!effectiveAddress.address.city;
  const canMerge = chosenOrders.length >= 2 && addressComplete && !merge.isPending && !mergedInto;

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function handleMerge() {
    if (!effectiveAddress) return;
    try {
      const meta = await merge.mutateAsync({
        orders: chosenOrders.map((o) => ({ storeId: o.storeId, orderId: o.orderId })),
        address: effectiveAddress.address,
      });
      setMergedInto(meta.number);
      setConfirmOpen(false);
      if (!meta.allSynced) {
        toast.warning(`${meta.number} napravljena, ali poštarina nije ažurirana na svim narudžbama — pokušajte "Ponovi ažuriranje"`);
      } else if (meta.emailError) {
        toast.warning(`${meta.number} napravljena, ali email nije poslan: ${meta.emailError}`);
      } else {
        toast.success(`Spojeno u ${meta.number}`);
      }
    } catch (e: any) {
      setConfirmOpen(false);
      toast.error(e?.message || "Spajanje nije uspjelo");
    }
  }

  return (
    <div
      ref={ref}
      id={`group-${group.id}`}
      className={cn(
        "ring-1 ring-foreground/10 bg-card scroll-mt-4 transition-shadow",
        mergedInto && "opacity-60",
        highlighted && "ring-2 ring-primary",
      )}
    >
      {/* Kupac */}
      <div className="flex flex-col gap-1.5 px-3 py-2.5 border-b border-foreground/10">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">{latest.customerName || [latest.address.firstName, latest.address.lastName].filter(Boolean).join(" ") || "Nepoznat kupac"}</span>
          <span className="text-xs text-muted-foreground">
            {group.orders.length} {plural(group.orders.length, "narudžba", "narudžbe", "narudžbi")}
          </span>
          <div className="flex items-center gap-1 flex-wrap ml-auto">
            <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mr-1">podudaranje</span>
            {group.reasons.map((r) => (
              <Badge key={r} variant="outline" className="bg-muted/40">{REASON_LABEL[r] ?? r}</Badge>
            ))}
            {group.weak && (
              <Badge variant="outline" className="bg-yellow-500/10 text-yellow-700 border-yellow-500/30">
                Provjeri — podudaranje samo po imenu
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-muted-foreground">
          {phones.length > 0 && (
            <span className="inline-flex items-center gap-1.5"><Phone className="size-3" />{phones.join(" / ")}</span>
          )}
          {emails.length > 0 && (
            <span className="inline-flex items-center gap-1.5 min-w-0 break-all"><Mail className="size-3 shrink-0" />{emails.join(" / ")}</span>
          )}
          {cities.length > 0 && (
            <span className="inline-flex items-center gap-1.5"><MapPin className="size-3" />{cities.join(" / ")}</span>
          )}
        </div>
      </div>

      {/* Narudžbe */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/40 text-muted-foreground">
              <th className="w-9 p-2" />
              <th className="p-2 text-left font-medium">Shop</th>
              <th className="p-2 text-left font-medium">Narudžba</th>
              <th className="p-2 text-right font-medium hidden sm:table-cell">Artikli</th>
              <th className="p-2 text-right font-medium">Iznos</th>
              <th className="p-2 text-right font-medium">Poštarina</th>
              <th className="p-2 text-left font-medium">Plaćanje</th>
              <th className="p-2 text-left font-medium hidden md:table-cell">Status</th>
            </tr>
          </thead>
          <tbody>
            {group.orders.map((o) => {
              const k = keyOf(o);
              const checked = selected.has(k);
              const target = preview.targets[k];
              const dropped = checked && target !== undefined && target < o.shippingTotal;
              return (
                <tr key={k} className={cn("border-b last:border-0", !checked && "text-muted-foreground")}>
                  <td className="p-2">
                    <Checkbox checked={checked} disabled={!!mergedInto || merge.isPending} onCheckedChange={() => toggle(k)} aria-label={`Označi ${o.storeName} #${o.number}`} />
                  </td>
                  <td className="p-2 font-medium whitespace-nowrap">{o.storeName}</td>
                  <td className="p-2 tabular-nums whitespace-nowrap">#{o.number}</td>
                  <td className="p-2 text-right tabular-nums hidden sm:table-cell">{o.itemCount} art.</td>
                  <td className="p-2 text-right tabular-nums whitespace-nowrap">{km(o.total)}</td>
                  <td className="p-2 text-right tabular-nums whitespace-nowrap">
                    {dropped ? (
                      <span>
                        <span className="line-through text-muted-foreground">{km(o.shippingTotal)}</span>{" "}
                        <span className="text-green-700 dark:text-green-500">{km(target!)}</span>
                      </span>
                    ) : (
                      km(o.shippingTotal)
                    )}
                  </td>
                  <td className="p-2 whitespace-nowrap" title={o.paymentTitle || undefined}>
                    {o.isPaid ? (
                      <Badge variant="outline" className="bg-green-500/10 text-green-700 border-green-500/20">Plaćeno</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-blue-500/10 text-blue-700 border-blue-500/20">Pouzeće</Badge>
                    )}
                  </td>
                  <td className="p-2 hidden md:table-cell"><StatusDot status={o.status} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Adresa dostave */}
      {addressOptions.length > 1 ? (
        <div className="px-3 py-2.5 border-t border-foreground/10 flex flex-col gap-2">
          <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Adrese se razlikuju — izaberi adresu dostave</span>
          <RadioGroup value={effectiveAddress?.key} onValueChange={setAddressChoice} className="gap-1.5">
            {addressOptions.map((opt) => (
              <label key={opt.key} className="flex items-start gap-2 text-xs cursor-pointer">
                <RadioGroupItem value={opt.key} className="mt-0.5" />
                <span>
                  {formatAddress(opt.address)}
                  <span className="text-muted-foreground"> · {opt.orders.map((o) => `${o.storeName} #${o.number}`).join(", ")}</span>
                </span>
              </label>
            ))}
          </RadioGroup>
        </div>
      ) : effectiveAddress ? (
        <div className="px-3 py-2 border-t border-foreground/10 text-xs">
          <span className="text-muted-foreground">Dostava na: </span>
          {formatAddress(effectiveAddress.address)}
        </div>
      ) : null}

      {/* Pregled i akcija */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-3 py-2.5 border-t border-foreground/10 bg-muted/25">
        {chosenOrders.length < 2 ? (
          <span className="text-xs text-muted-foreground">Označi bar 2 narudžbe za spajanje.</span>
        ) : (
          <div className="flex flex-col gap-0.5 text-xs">
            <span>
              {preview.keeper ? (
                <>Poštarinu zadržava <b>{preview.keeper.storeName} #{preview.keeper.number}</b> ({km(preview.keeper.shippingTotal)})</>
              ) : preview.hasCod ? (
                preview.freeShipping
                  ? "Besplatna dostava — pouzeće narudžbe idu bez poštarine"
                  : "Poštarina je već plaćena karticom — pouzeće narudžbe idu bez poštarine"
              ) : (
                "Sve narudžbe su plaćene — poštarina se ne mijenja"
              )}
            </span>
            <span className="text-muted-foreground">
              Otkupnina nakon spajanja: <b className="text-foreground tabular-nums">{km(preview.newCod)}</b>
              {preview.savings > 0 && (
                <> · ušteda kupca <b className="text-green-700 dark:text-green-500 tabular-nums">{km(preview.savings)}</b></>
              )}
            </span>
          </div>
        )}
        <Button size="sm" disabled={!canMerge} onClick={() => setConfirmOpen(true)} className="self-start sm:self-auto">
          <Combine />
          {mergedInto ? `Spojeno u ${mergedInto}` : "Spoji u jednu pošiljku"}
        </Button>
      </div>
      {chosenOrders.length >= 2 && !addressComplete && (
        <p className="px-3 pb-2.5 text-xs text-red-600">Izabrana adresa nema ime, ulicu ili grad — ne može se spojiti.</p>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={(v) => { if (!merge.isPending) setConfirmOpen(v); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Spojiti {chosenOrders.length} {plural(chosenOrders.length, "narudžbu", "narudžbe", "narudžbi")} u jednu pošiljku?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="flex flex-col gap-2">
                <span>{chosenOrders.map((o) => `${o.storeName} #${o.number}`).join(", ")}</span>
                {preview.savings > 0 && (
                  <span>Poštarina se u WooCommerceu skida sa narudžbi koje je ne zadržavaju (ukupno {km(preview.savings)}).</span>
                )}
                {preview.hasCod && <span>Otkupnina za jedan paket: <b>{km(preview.newCod)}</b>.</span>}
                {effectiveAddress && <span>Dostava na: {formatAddress(effectiveAddress.address)}</span>}
                <span>
                  {recipientEmails.length > 0
                    ? <>Kupac će dobiti email s objašnjenjem na {recipientEmails.join(", ")}.</>
                    : "Narudžbe nemaju email kupca, pa email neće biti poslan."}
                </span>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={merge.isPending}>Odustani</AlertDialogCancel>
            <AlertDialogAction
              disabled={merge.isPending}
              onClick={(e) => {
                e.preventDefault();
                void handleMerge();
              }}
            >
              {merge.isPending && <Loader2 className="size-4 animate-spin" />}
              Spoji
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
